<template>
  <v-container id="missionDetail" fluid>
    <v-row>
      <GoToBanner v-if="isParent" path="KidQuests" :params="goBackLinkParams" class="mt-8" />
      <GoToBanner v-else path="ChildDashboard" class="mt-8" />
    </v-row>
    <v-row>
      <NameInfoBanner :name="kid.name" :avatar="kid.avatar" :points="kid.points" />
    </v-row>
    <v-row v-if="mission">
      <v-card flat tile class="full-width detail-view-container py-0">
        <!-- Mission Detail List -->
        <v-row v-if="!isEditing">
          <v-col cols="12" md="6">
            <v-card-title>
              <!-- Mission Name -->
              <v-list-item class="body-heading-large">
                {{ mission.name }}
                <v-btn v-if="isParent" @click="toggleEditing" icon class="ma-1">
                  <v-icon size="16">fas fa-edit</v-icon>
                </v-btn>
              </v-list-item>
            </v-card-title>
            <v-card-text>
              <!-- Mission Deadline -->
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-title class="body-heading-medium mb-3">Quest Deadline:</v-list-item-title>
                  <v-list-item-subtitle>{{ mission.due_date }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <!-- Mission Description -->
              <v-list-item>
                <v-list-item-content class="quest-description">
                  <v-list-item-title class="body-heading-medium mb-3">Quest Description:</v-list-item-title>
                  <v-list-item-subtitle>{{ mission.description }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <!-- Mission Point Value -->
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-title class="body-heading-medium mb-3">Point Value:</v-list-item-title>
                  <v-list-item-subtitle>{{ mission.points }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <!-- Mission Recurring Status -->
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-title class="body-heading-medium mb-3">Recurring Quest?</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-btn-toggle v-model="mission.reoccur_scale" dense>
                      <v-btn disabled value="days">Daily</v-btn>
                      <v-btn disabled value="weeks">Weekly</v-btn>
                      <v-btn disabled value="months">Monthly</v-btn>
                    </v-btn-toggle>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-card-text>
          </v-col>
          <v-col cols="12" md="6">
            <v-card flat>
              <v-card-title class="d-flex align-center justify-center justify-md-end">
                <MissionActions v-if="!isEditing" :mission="mission" />
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>

        <!-- Edit Mission Detail Form -->
        <v-row v-else>
          <v-col cols="12" sm="12" md="12" class="d-flex justify-center">
            <v-form :class="{ 'half-width': !isMobile }" v-model="isMissionUpdateFormValid" ref="updateMissionForm" v-on:submit.prevent="updateMission">
              <!-- Name & Deadline -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field label="Quest Name" name="mission-name" type="text" outlined dense required :rules="requiredFieldRules" v-model="newMissionDetail.name" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu ref="menu" v-model="isMissionDueDatePickerOpen" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field v-model="newMissionDetail.due_date" label="Deadline" outlined dense readonly v-on="on" required :rules="requiredFieldRules"></v-text-field>
                    </template>
                    <v-date-picker
                      ref="picker"
                      v-model="newMissionDetail.due_date"
                      :min="
                        this.$moment()
                          .toISOString(true)
                          .substr(0, 10)
                      "
                      @change="selectMissionDueDate"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <!-- Description -->
              <v-row>
                <v-col cols="12" md="12">
                  <v-textarea label="Quest Description" name="mission-description" type="text" counter="500" rows="5" auto-grow outlined dense required :rules="descriptionFieldRules" v-model="newMissionDetail.description" />
                </v-col>
              </v-row>
              <!-- Points -->
              <v-row>
                <v-col cols="12" md="12">
                  <v-text-field label="Points Value" name="mission-point-value" type="number" outlined dense :rules="pointsFieldRules" v-model="newMissionDetail.points" min="1" />
                </v-col>
              </v-row>
              <!-- Recurring -->
              <v-row>
                <v-col cols="12" md="12">
                  <p>Recurring Quest?</p>
                  <v-btn-toggle v-model="newMissionDetail.reoccur_scale" dense @change="setRecurringMission">
                    <v-btn value="days">Daily</v-btn>
                    <v-btn value="weeks">Weekly</v-btn>
                    <v-btn value="months">Monthly</v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
              <!-- Actions -->
              <v-row>
                <v-col cols="12" md="6" class="d-flex justify-md-start justify-center">
                  <v-btn color="secondary" type="submit" :disabled="!isMissionUpdateFormValid">
                    Save
                  </v-btn>
                  <v-btn text @click="cancel">
                    Cancel
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-col>
        </v-row>
      </v-card>
    </v-row>
  </v-container>
</template>

<script>
  import GoToBanner from "@/components/GoToBanner";
  import NameInfoBanner from "@/components/Kids/NameInfoBanner";
  import MissionActions from "./MissionActions";

  export default {
    name: "MissionDetail",
    data() {
      return {
        kid: {},
        isEditing: false,
        descriptionFieldRules: [(v) => !!v || "Field cannot be empty.", (v) => (v && v.length <= 500) || "Sorry, the maximum characters amount is 500"],
        requiredFieldRules: [(v) => !!v || "Field cannot be empty."],
        pointsFieldRules: [(v) => !!v || "Field cannot be empty.", (v) => v >= 1 || "Points value should at least be 1"],
        isMissionUpdateFormValid: false,
        isMissionDueDatePickerOpen: false,
        isMissionReoccuring: false,
        missionReoccurFrequency: 1,
        newMissionDetail: {},
      };
    },
    components: {
      GoToBanner,
      MissionActions,
      NameInfoBanner,
    },
    methods: {
      kidFetchMissions() {
        this.$store.commit("setLoadingStatus", true);
        this.$http
          .get("/missions")
          .then((response) => {
            this.$store.commit("addMissions", response.data.missions);
            this.$store.commit("setLoadingStatus", false);
          })
          .catch((error) => {
            console.error(error);
            this.$store.commit("setError", {
              message: "Failed to fetch quests",
              type: "error",
            });
            this.$store.commit("setLoadingStatus", false);
          });
      },

      parentFetchMissions() {
        this.$store.commit("setLoadingStatus", true);
        this.$http
          .get(`/children/${this.$route.params.id}`)
          .then((response) => {
            this.kid = response.data.child;

            this.$store.commit("addMissions", response.data.missions);
            this.newMissionDetail = this.$_.extend({}, this.mission);

            this.$store.commit("setLoadingStatus", false);
          })
          .catch(() => {
            this.$store.commit("setError", {
              message: "Failed to fetch quests",
              type: "error",
            });
            this.$store.commit("setLoadingStatus", false);
          });
      },

      toggleEditing() {
        this.newMissionDetail = this.$_.extend({}, this.mission);
        this.isEditing = true;
      },

      selectMissionDueDate(date) {
        this.$refs.menu.save(date);
      },

      setRecurringMission() {
        //FIXME: this should be selected by user
        this.isMissionReoccuring = true;
        this.missionReoccurFrequency = 1;
      },

      updateMission() {
        this.$store.commit("setLoadingStatus", true);

        this.$http
          .put(`/missions/${this.mission.id}`, { mission: this.newMissionDetail })
          .then((response) => this.updateMissionSuccess(response))
          .catch((error) => this.updateMissionFail(error));
      },

      updateMissionSuccess(response) {
        this.$store.commit("updateMission", response.data.mission);
        this.$store.commit("setLoadingStatus", false);
        this.isEditing = false;
      },

      updateMissionFail(error) {
        console.error(error);
        this.$store.commit("setLoadingStatus", false);
        this.isEditing = false;
      },

      cancel() {
        this.newMissionDetail = {};
        this.isEditing = false;
      },
    },
    created() {
      if (this.isParent) {
        this.parentFetchMissions();
      }
      if (this.isKid) {
        this.kidFetchMissions();
      }
    },
    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.mobile;
      },
      isParent() {
        return this.$store.getters.userInfo.role === "parent";
      },
      isKid() {
        return this.$store.getters.userInfo.role === "child";
      },
      mission() {
        return this.$store.getters.findMissionById(this.$route.params.missionId);
      },
      goBackLinkParams() {
        return { id: this.kid.id };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .quest-description {
    white-space: normal !important;
    overflow-y: scroll !important;

    .v-list-item__subtitle {
      white-space: normal !important;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Mission/MissionDetail.vue